import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import gsap from 'gsap';
import { useGlobal } from '../../context/GlobalContext';
import { getStrapiMediaAttributes } from '../../lib/strapi';
import { getUiText } from '../../lib/uiText';
import maximizeIcon from '../../assets/icons/icon_flecha_maximizar.svg';
import styles from './Gallery.module.css';

const Gallery = ({ data }) => {
  const { locale } = useGlobal();
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [lightboxClosing, setLightboxClosing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const sectionRef = useRef(null);
  const slidesRef = useRef([]);
  const lightboxRef = useRef(null);
  const intervalRef = useRef(null);
  const resumeTimeoutRef = useRef(null);
  const hasInitialized = useRef(false);
  const suppressClickRef = useRef(false);

  const items = data?.items || [];
  const isGradientEnabled = data?.degradado !== false;
  const total = items.length;
  const rawSpeed = Number(data?.autoplaySpeed || 4000);
  const speed = Number.isFinite(rawSpeed) && rawSpeed > 0
    ? (rawSpeed <= 60 ? rawSpeed * 1000 : rawSpeed)
    : 4000;
  const lightboxImage = lightbox !== null
    ? getStrapiMediaAttributes(items[lightbox]?.image, {
      altFallback: items[lightbox]?.title || data?.title || '',
      titleFallback: items[lightbox]?.title || data?.title || '',
    })
    : null;

  /* ── Detect mobile for swipe & bigger center slide ── */
  const [isMobile, setIsMobile] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  /* ── Viewport observer — start only when fully visible ── */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.6 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* ── Reveal-once observer for entrance animation (mirrors Tipologias) ── */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !('IntersectionObserver' in window)) {
      setIsRevealed(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.2, rootMargin: '0px 0px -50px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* ── circular offset: -1 = prev, 0 = center, 1 = next ── */
  const getOffset = useCallback(
    (i, center) => {
      if (total === 0) return Infinity;
      let d = i - center;
      if (d > total / 2) d -= total;
      if (d < -total / 2) d += total;
      return d;
    },
    [total]
  );

  /* ── position every slide with GSAP ── */
  const positionSlides = useCallback(
    (center, animate) => {
      if (total === 0) return;
      const dur = animate ? 0.65 : 0;
      const base = {
        top: '50%',
        yPercent: -50,
        xPercent: -50,
        ease: 'power2.inOut',
      };

      for (let i = 0; i < total; i++) {
        const el = slidesRef.current[i];
        if (!el) continue;
        const off = getOffset(i, center);

        if (off === 0) {
          gsap.to(el, {
            ...base,
            left: '50%',
            width: isMobile ? '85%' : '55%',
            height: isMobile ? '80%' : '90%',
            opacity: 1,
            zIndex: 3,
            boxShadow: '0 1em 2em rgba(0,0,0,0.2)',
            duration: dur,
            pointerEvents: 'auto',
          });
        } else if (off === -1) {
          gsap.to(el, {
            ...base,
            left: isMobile ? '3%' : '15%',
            width: isMobile ? '18%' : '28%',
            height: isMobile ? '55%' : '78%',
            opacity: isMobile ? 0.3 : 0.5,
            zIndex: 2,
            boxShadow: '0 0.5em 1em rgba(0,0,0,0.1)',
            duration: dur,
            pointerEvents: 'auto',
          });
        } else if (off === 1) {
          gsap.to(el, {
            ...base,
            left: isMobile ? '97%' : '85%',
            width: isMobile ? '18%' : '28%',
            height: isMobile ? '55%' : '78%',
            opacity: isMobile ? 0.3 : 0.5,
            zIndex: 2,
            boxShadow: '0 0.5em 1em rgba(0,0,0,0.1)',
            duration: dur,
            pointerEvents: 'auto',
          });
        } else {
          gsap.to(el, {
            ...base,
            left: off < 0 ? (isMobile ? '-18%' : '-10%') : (isMobile ? '118%' : '110%'),
            width: isMobile ? '18%' : '28%',
            height: isMobile ? '55%' : '78%',
            opacity: 0,
            zIndex: 0,
            duration: dur * 0.4,
            boxShadow: '0 0 0 rgba(0,0,0,0)',
            pointerEvents: 'none',
          });
        }
      }
    },
    [total, getOffset, isMobile]
  );

  /* ── initialization (no animation) ── */
  useEffect(() => {
    if (total > 0 && !hasInitialized.current) {
      positionSlides(0, false);
      hasInitialized.current = true;
    }
  }, [total, positionSlides]);

  /* ── animate on slide change ── */
  useEffect(() => {
    if (hasInitialized.current) {
      positionSlides(current, true);
    }
  }, [current, positionSlides]);

  /* ── autoplay — only when in viewport ── */
  useEffect(() => {
    if (total <= 1 || isPaused || !isInView) return;
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, speed);
    return () => clearInterval(intervalRef.current);
  }, [current, total, speed, isPaused, isInView]);

  /* ── lightbox open with fade ── */
  const openLightbox = (idx) => {
    setIsPaused(true);
    clearInterval(intervalRef.current);
    setLightbox(idx);
    setLightboxClosing(false);
  };

  /* ── lightbox close with fade ── */
  const closeLightbox = useCallback(() => {
    if (lightboxClosing) return;
    setLightboxClosing(true);
    const el = lightboxRef.current;
    if (el) {
      gsap.to(el, {
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in',
        onComplete: () => {
          setLightbox(null);
          setLightboxClosing(false);
          clearTimeout(resumeTimeoutRef.current);
          resumeTimeoutRef.current = setTimeout(() => setIsPaused(false), 3000);
        },
      });
    } else {
      setLightbox(null);
      setLightboxClosing(false);
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = setTimeout(() => setIsPaused(false), 3000);
    }
  }, [lightboxClosing]);

  /* ── cleanup ── */
  useEffect(
    () => () => {
      clearInterval(intervalRef.current);
      clearTimeout(resumeTimeoutRef.current);
    },
    []
  );

  /* ── keyboard ── */
  useEffect(() => {
    if (lightbox === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox, closeLightbox]);

  useEffect(() => {
    if (lightbox === null) return undefined;

    const { body, documentElement } = document;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlOverflow = documentElement.style.overflow;

    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousBodyOverflow;
      documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [lightbox]);

  /* ── Swipe handlers for mobile ── */
  const handleTouchStart = (e) => {
    if (!isMobile || total <= 1) return;

    suppressClickRef.current = false;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    if (!isMobile || total <= 1) return;

    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
  };

  const queueResume = useCallback((delay = 5000) => {
    clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => setIsPaused(false), delay);
  }, []);

  const stepToIndex = useCallback((index) => {
    setIsPaused(true);
    clearInterval(intervalRef.current);
    setCurrent(index);
    queueResume();
  }, [queueResume]);

  const stepNext = useCallback(() => {
    stepToIndex((current + 1) % total);
  }, [current, stepToIndex, total]);

  const stepPrev = useCallback(() => {
    stepToIndex((current - 1 + total) % total);
  }, [current, stepToIndex, total]);

  if (!items.length) return null;

  const handleTouchEnd = () => {
    if (!isMobile || total <= 1) return;

    const deltaX = touchStartX.current - touchEndX.current;
    const deltaY = Math.abs(touchStartY.current - touchEndY.current);
    const minSwipe = 50;

    // Only trigger if horizontal swipe is dominant
    if (Math.abs(deltaX) > minSwipe && Math.abs(deltaX) > deltaY) {
      suppressClickRef.current = true;
      if (deltaX > 0) {
        // Swipe left → next
        stepNext();
      } else {
        // Swipe right → prev
        stepPrev();
      }
    }
    touchEndX.current = 0;
    touchEndY.current = 0;
  };

  return (
    <section
      ref={sectionRef}
      className={`${styles.gallery} ${isRevealed ? styles['gallery--visible'] : ''}`}
    >
      {data?.title && <h2 className={styles.gallery__title}>{data.title}</h2>}

      <div
        className={styles.gallery__viewport}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className={styles.gallery__track}>
          {items.map((item, i) => {
            const imageMedia = getStrapiMediaAttributes(item?.image, {
              altFallback: item?.title || data?.title || '',
              titleFallback: item?.title || data?.title || '',
            });
            if (!imageMedia.src) return null;

            const showSlideOverlay = Boolean(item.title) || isGradientEnabled;

            const offset = getOffset(i, current);

            const handleSlideClick = () => {
              if (suppressClickRef.current) {
                suppressClickRef.current = false;
                return;
              }

              if (offset === 0) {
                openLightbox(i);
              } else if (offset === -1 || offset === 1) {
                stepToIndex(i);
              }
            };

            return (
              <div
                key={`slide-${i}`}
                ref={(el) => (slidesRef.current[i] = el)}
                className={styles.gallery__slide}
                onClick={handleSlideClick}
              >
                <img
                  src={imageMedia.src}
                  alt={imageMedia.alt}
                  title={imageMedia.title}
                  className={styles.gallery__image}
                  draggable={false}
                />
                {showSlideOverlay && (
                  <div
                    className={`${styles.gallery__titleOverlay} ${
                      !isGradientEnabled ? styles['gallery__titleOverlay--transparent'] : ''
                    }`}
                  >
                    {item.title && (
                      <span className={styles.gallery__imageTitle}>
                        {item.title}
                      </span>
                    )}
                  </div>
                )}
                <img
                  src={maximizeIcon}
                  alt={getUiText(locale, 'maximize')}
                  title={getUiText(locale, 'maximize')}
                  className={styles.gallery__maximizeIcon}
                  draggable={false}
                />
              </div>
            );
          })}
        </div>
      </div>

      {total > 1 && (
        <div className={styles.gallery__dots}>
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`${styles.gallery__dot} ${
                i === current ? styles['gallery__dot--active'] : ''
              }`}
              onClick={() => stepToIndex(i)}
              aria-label={getUiText(locale, 'imageIndex', { index: i + 1 })}
            />
          ))}
        </div>
      )}

      {lightbox !== null && createPortal(
        <div
          ref={lightboxRef}
          className={styles.gallery__lightbox}
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={items[lightbox]?.title || data?.title || getUiText(locale, 'expandedImage')}
        >
          <button
            type="button"
            className={styles.gallery__lightboxClose}
            onClick={closeLightbox}
            aria-label={getUiText(locale, 'close')}
          >
            &times;
          </button>
          <div
            className={styles.gallery__lightboxContent}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage?.src}
              alt={lightboxImage?.alt || ''}
              title={lightboxImage?.title}
              className={styles.gallery__lightboxImage}
            />
            {(items[lightbox]?.title || isGradientEnabled) && (
              <div
                className={`${styles.gallery__lightboxTitleOverlay} ${
                  !isGradientEnabled ? styles['gallery__lightboxTitleOverlay--transparent'] : ''
                }`}
              >
                {items[lightbox]?.title && (
                  <span className={styles.gallery__lightboxTitle}>
                    {items[lightbox].title}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default Gallery;
